import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminNotificationCenterTemplate } from '@/components/templates';
import {
  useCurrentSite,
  useNotificationBulkAction,
  useNotificationList,
  useNotificationUnreadCounts,
} from '@/hooks/admin';
import useAppModal from '@/hooks/useAppModal';
import {
  downloadDriftCsv,
  downloadErrorCsv,
  downloadRunErrorCsv,
  isLiveTab,
} from '@/services/admin/notification-center';
import { messageFromBlobError } from '@/utils/blobDownload';
import {
  NOTIFICATION_TAB,
  DEFAULT_PER_PAGE,
  type NotificationFilters,
  type NotificationItem,
  type NotificationTab,
} from '@/types/admin-notification-center';

const TABS: NotificationTab[] = [
  NOTIFICATION_TAB.MASTER,
  NOTIFICATION_TAB.REZEPT,
  NOTIFICATION_TAB.INBOUND,
];

/**
 * 通知センター.
 *
 * ALL THREE TABS read the real API (services/admin/notification-center, REAL_TABS). The page is written
 * against one set of hook signatures and does not know or care where a tab's rows came from.
 *
 * The filters live in the URL, not in component state: this screen is shared by pasting a link — 「look at
 * this clinic's failures for August」 — and a filter that only exists in memory cannot be sent to anyone.
 */
const NotificationCenterPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const appModal = useAppModal();
  const site = useCurrentSite();

  const tabParam = searchParams.get('tab') as NotificationTab | null;
  const tab: NotificationTab = tabParam && TABS.includes(tabParam) ? tabParam : NOTIFICATION_TAB.MASTER;
  const page = Math.max(1, Number(searchParams.get('page') || '1'));
  // 20 A PAGE, FIXED. The Hitowa common spec gives every list one page size and no control to change it,
  // so `per_page` is neither read from the URL nor written to it — a hand-typed ?per_page=5000 now changes
  // nothing rather than being validated down.
  const perPage = DEFAULT_PER_PAGE;

  const filters: NotificationFilters = useMemo(
    () => ({
      from: searchParams.get('from') || '',
      to: searchParams.get('to') || '',
      read: (searchParams.get('read') as NotificationFilters['read']) || 'all',
      // Repeated key rather than a comma list — getAll cannot be tricked by a value that contains one.
      //
      // 🔴 IGNORED ON 審査システムからの通知. 種別 means two different things across the tabs: an OUTCOME
      // (成功 / エラー / 情報) on the two sending inboxes, and WHICH MESSAGE 審査 sent (返戻 / 一部不支給 /
      // 返戻取消 / 消込解除) on this one. Every inbound row is 情報, so a 「エラー」 carried over from another
      // tab emptied the list while the tab badge still said 3 — and the inbound filter panel has no 種別
      // control to clear it with, so the only escape was 条件をクリア, which nothing told the reader to press.
      //
      // DROPPED AT READ TIME, not on the tab switch, because the same trap arrives through a pasted link.
      // The value is left in the query string, so going back to a tab where 種別 means something applies it
      // again — until the reader runs a search from the inbound panel, which rewrites the query string from
      // the filters this tab can actually show. The five shared controls carry across on purpose; this one
      // is shared in name only.
      levels:
        tab === NOTIFICATION_TAB.INBOUND
          ? []
          : (searchParams.getAll('level') as NotificationFilters['levels']),
      // THE MIRROR OF THE LINE ABOVE. 種別 on this tab is the KIND, and it is a separate query key so the
      // two meanings can never overwrite each other in a URL. Dropped on the sending tabs for the same
      // reason `level` is dropped here: their panel shows the other 種別, so a kind carried across would
      // narrow a list with a condition the reader can see no control for.
      kinds:
        tab === NOTIFICATION_TAB.INBOUND
          ? (searchParams.getAll('kind') as NotificationFilters['kinds'])
          : [],
      clinicNumber: searchParams.get('clinic_no') || '',
      clinicName: searchParams.get('clinic_name') || '',
      serviceType: searchParams.get('service') || '',
      patient: searchParams.get('patient') || '',
    }),
    [searchParams, tab]
  );

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const listQuery = { ...filters, tab, page, perPage };
  const { data, isFetching } = useNotificationList(listQuery);
  const { data: unreadCounts } = useNotificationUnreadCounts();
  const bulk = useNotificationBulkAction();

  const rows = useMemo(() => data?.data ?? [], [data]);

  /**
   * Selection is dropped whenever the visible set changes — new tab, new page, new search.
   *
   * This is a safety rule, not tidiness: the bulk buttons act on an id list, so a tick left over from a page
   * the operator has since navigated away from would be deleted by a press aimed at what is on screen now.
   */
  useEffect(() => {
    setSelectedIds([]);
  }, [tab, page, searchParams]);

  const writeParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams);
      mutate(params);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleTabChange = (next: NotificationTab) =>
    writeParams(params => {
      params.set('tab', next);
      params.set('page', '1');
      // Each of these belongs to some tabs and not others — キー only to マスタ同期, 治療院ID and 施術区分
      // only to the two that have a clinic, 受診者 only to the inbound tab. Carrying one across would filter
      // the new tab by a condition its own panel cannot show, let alone clear.
      // 施術区分 belongs to レセプト同期 and 受診者 to 審査システムからの通知; carrying either across would
      // filter the new tab by a condition its own panel cannot show, let alone clear. The five shared
      // controls (種別・処理時間・治療院ID・治療院名・既読状態) DO carry across on purpose — that is the
      // point of them being the same five everywhere.
      params.delete('service');
      params.delete('patient');
    });

  const handleFiltersChange = (next: NotificationFilters) =>
    writeParams(params => {
      const put = (name: string, value: string) =>
        value ? params.set(name, value) : params.delete(name);

      put('from', next.from);
      put('to', next.to);
      put('read', next.read === 'all' ? '' : next.read);
      put('clinic_no', next.clinicNumber.trim());
      put('clinic_name', next.clinicName.trim());
      put('patient', next.patient.trim());

      params.delete('level');
      next.levels.forEach(value => params.append('level', value));

      params.delete('kind');
      next.kinds.forEach(value => params.append('kind', value));

      put('service', next.serviceType);

      // A narrower search can leave the current page past the end of the result set.
      params.set('page', '1');
    });

  const handleLevelShortcut = (levels: NotificationFilters['levels']) =>
    handleFiltersChange({ ...filters, levels });

  const handlePageChange = (next: number) => writeParams(params => params.set('page', String(next)));

  /* ------------------------------------------------------------------------------ bulk actions */

  const isInbound = tab === NOTIFICATION_TAB.INBOUND;
  const selectedOnPage = rows.filter(item => selectedIds.includes(item.id));
  const retryable = selectedOnPage.filter(item => item.retryable);

  const handleBulkMarkRead = () => bulk.mutate({ action: 'markRead', ids: selectedIds, tab });

  /**
   * 再送信 overwrites whatever 審査 currently holds for those records, including anything typed there by
   * hand. Same warning the existing 同期通知 screen gives, for the same reason.
   */
  const confirmRetry = (ids: number[], count: number) =>
    appModal.confirm({
      title: `${count}件を${isInbound ? '再取込' : '再送信'}します`,
      content: isInbound ? (
        <p className="m-0">審査システムから届いた通知をもう一度取り込みます。よろしいですか？</p>
      ) : (
        <>
          <p>審査システムへ再送信します。現在の内容で上書きされます。よろしいですか？</p>
          <p className="m-0 text-sm text-gray-500">
            審査側で手入力された内容は上書きされますが、審査結果・返戻・入金の情報は保持されます。
          </p>
        </>
      ),
      okText: 'はい',
      cancelText: 'いいえ',
      onOk: () => bulk.mutate({ action: 'retry', ids, tab, verb: isInbound ? '再取込' : '再送信' }),
    });

  const handleBulkRetry = () =>
    confirmRetry(
      retryable.map(item => item.id),
      retryable.length
    );

  /**
   * The server's own refusal, or a generic line when the request never got an answer.
   *
   * 🔴 WHY THE SERVER'S WORDS RATHER THAN OUR OWN. Every refusal these two endpoints can give has a
   * sentence written for it in BD §CSV — 73.0 「通知が見つかりません。」, 74.0 「明細ファイルが見つかりません。
   * 次回の照合後に再度お試しください。」, 75.0 「明細ファイルを生成できませんでした。」 — and they differ in
   * what the reader should DO: wait for tonight's reconcile, or call support. A single hand-written line
   * here (which is what the drift button used to show) collapses that distinction and sends people to
   * press the same button again on a file that will not exist until tomorrow.
   *
   * The request is `responseType: 'blob'`, so the body has to be read back out of a Blob to reach it —
   * see messageFromBlobError.
   */
  const showDownloadFailure = async (error: unknown): Promise<void> => {
    appModal.error({
      content: (await messageFromBlobError(error)) ?? 'ファイルをダウンロードできませんでした。',
    });
  };

  /**
   * ① エラー詳細. The file comes from the SERVER on a live tab and from the row itself on a mocked one. The
   * server version is the one that matters: it reads sync_audit_logs, which keeps every attempt of every
   * failed rezept, while the row on screen only ever carries one summary sentence for the whole press.
   *
   * THE CATCH IS NEW. This handler used to hand its promise to nobody, so a 404 — a row outside this
   * admin's sites, or an id that no longer exists — became an unhandled rejection and the button simply
   * did nothing when pressed. 73.0 never reached anyone.
   */
  const handleDownloadError = async (item: NotificationItem) => {
    if (!isLiveTab(tab)) {
      downloadErrorCsv([item]);
      return;
    }

    try {
      await downloadRunErrorCsv(site, item.id);
    } catch (error) {
      await showDownloadFailure(error);
    }
  };

  /**
   * ②/③ 不一致明細CSV — its own endpoint, not the error one. A site row answers with the ZIP of every
   * clinic file of that run; anything older answers with the single file.
   *
   * The button is only drawn where the row says a file exists, so a failure here means the file went away
   * since the page loaded (a clean-up, or a reconcile run that could not write) — which is exactly the
   * difference 74.0 and 75.0 carry, so the server says it rather than this.
   */
  const handleDownloadDrift = async (item: NotificationItem) => {
    try {
      await downloadDriftCsv(site, item.id);
    } catch (error) {
      await showDownloadFailure(error);
    }
  };

  return (
    <AdminNotificationCenterTemplate
      breadcrumbItems={[{ title: '通知センター' }]}
      tab={tab}
      onTabChange={handleTabChange}
      unreadCounts={unreadCounts}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      data={rows}
      meta={data?.meta}
      summary={data?.summary}
      isLoading={isFetching}
      onPageChange={handlePageChange}
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      onBulkRetry={handleBulkRetry}
      onBulkMarkRead={handleBulkMarkRead}
      onDownloadError={handleDownloadError}
      onDownloadDrift={handleDownloadDrift}
      onLevelShortcut={handleLevelShortcut}
      isBusy={bulk.isPending}
    />
  );
};

export default NotificationCenterPage;
